/**
 * VideoConsultPage.tsx — 1:1 화상 상담 페이지
 * Route: /consult/:consultId?role=broker|investor
 * NDA 동의 완료 투자자 ↔ 중개사 화상 상담
 */

import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useWebRTC } from '../../hooks/useWebRTC';
import { useMediaStream } from '../../hooks/useMediaStream';

const RTDB_PATH = 'land-consults';

export default function VideoConsultPage() {
  const { consultId = '' } = useParams<{ consultId: string }>();
  const [params]           = useSearchParams();
  const role               = params.get('role') === 'broker' ? 'broker' : 'investor';
  const navigate           = useNavigate();

  const media = useMediaStream();
  const rtc   = useWebRTC();

  const localRef  = useRef<HTMLVideoElement>(null);
  const remoteRef = useRef<HTMLVideoElement>(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError]     = useState('');

  // 카메라/마이크 시작
  useEffect(() => {
    media.start(false).then(stream => {
      if (localRef.current) localRef.current.srcObject = stream;

      if (role === 'broker') {
        rtc.createRoom(consultId, stream, RTDB_PATH).catch(e => setError(String(e)));
      } else {
        rtc.joinRoom(consultId, stream, RTDB_PATH).catch(e => setError(String(e)));
      }
    }).catch(e => setError('카메라/마이크 권한이 필요합니다: ' + String(e)));

    return () => {
      media.stop();
      rtc.hangup(consultId, RTDB_PATH).catch((e) => console.warn('hangup cleanup failed:', e));
    };
  }, []);

  // 원격 영상 연결
  useEffect(() => {
    if (remoteRef.current && rtc.remoteStream) {
      remoteRef.current.srcObject = rtc.remoteStream;
    }
  }, [rtc.remoteStream]);

  // 타이머
  useEffect(() => {
    if (rtc.connState !== 'connected') return;
    const id = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(id);
  }, [rtc.connState]);

  const handleHangup = async () => {
    media.stop();
    await rtc.hangup(consultId, RTDB_PATH);
    navigate(-1);
  };

  const connLabel = rtc.connState === 'connected'
    ? `통화 중 ${String(Math.floor(elapsed / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`
    : rtc.connState === 'connecting'
    ? '연결 중...'
    : rtc.connState === 'closed'
    ? '통화 종료'
    : role === 'broker' ? '투자자 연결 대기 중...' : '연결 중...';

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a12', color: '#f1f5f9', display: 'flex', flexDirection: 'column' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* 헤더 */}
      <div style={{
        padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10,
        borderBottom: '1px solid #1e2030', background: '#0d1117',
      }}>
        <span style={{ fontWeight: 700 }}>
          {role === 'broker' ? '🏠 매물 화상 상담 (중개사)' : '💼 매물 화상 상담 (투자자)'}
        </span>
        <span style={{
          marginLeft: 'auto',
          color: rtc.connState === 'connected' ? '#22c55e' : '#f59e0b',
          fontSize: '0.8rem', fontWeight: 700,
        }}>
          {connLabel}
        </span>
      </div>

      {/* 영상 영역 */}
      <div style={{ flex: 1, position: 'relative', background: '#000' }}>
        {/* 원격 (전체) */}
        <video
          ref={remoteRef}
          autoPlay playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />

        {/* 연결 대기 오버레이 */}
        {!rtc.remoteStream && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 16,
            background: '#0a0a12',
          }}>
            <div style={{
              width: 64, height: 64, border: '5px solid #1e2030',
              borderTopColor: '#1d4ed8', borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <p style={{ color: '#64748b' }}>{connLabel}</p>
            {error && <p style={{ color: '#ef4444', fontSize: '0.8rem' }}>{error}</p>}

            {role === 'broker' && (
              <div style={{
                background: '#0d1117', border: '1px solid #1e2030',
                borderRadius: 10, padding: '12px 20px', textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 6 }}>상담 링크 공유</div>
                <div style={{
                  background: '#111827', borderRadius: 6, padding: '6px 12px',
                  fontSize: '0.78rem', color: '#94a3b8', wordBreak: 'break-all',
                }}>
                  {`${window.location.origin}/consult/${consultId}?role=investor`}
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/consult/${consultId}?role=investor`);
                  }}
                  style={{
                    marginTop: 8, padding: '6px 16px', borderRadius: 6, border: 'none',
                    background: '#1d4ed8', color: '#fff', fontSize: '0.75rem', cursor: 'pointer',
                  }}
                >
                  링크 복사
                </button>
              </div>
            )}
          </div>
        )}

        {/* 내 영상 PIP */}
        <div style={{
          position: 'absolute', bottom: 100, right: 16,
          width: 160, height: 120,
          borderRadius: 10, overflow: 'hidden',
          border: '2px solid #374151',
        }}>
          <video
            ref={localRef}
            autoPlay playsInline muted
            style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
          />
        </div>
      </div>

      {/* 컨트롤 바 */}
      <div style={{
        padding: '12px 20px', display: 'flex', justifyContent: 'center', gap: 16,
        background: '#0d1117', borderTop: '1px solid #1e2030',
      }}>
        <CtrlBtn
          icon={media.audioEnabled ? '🎙' : '🔇'}
          label={media.audioEnabled ? '마이크 ON' : '마이크 OFF'}
          active={media.audioEnabled}
          onClick={media.toggleAudio}
        />
        <CtrlBtn
          icon={media.videoEnabled ? '📹' : '📷'}
          label={media.videoEnabled ? '카메라 ON' : '카메라 OFF'}
          active={media.videoEnabled}
          onClick={media.toggleVideo}
        />
        <button
          onClick={handleHangup}
          style={{
            padding: '12px 32px', borderRadius: 50, border: 'none',
            background: '#dc2626', color: '#fff',
            fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
          }}
        >
          📵 통화 종료
        </button>
      </div>
    </div>
  );
}

function CtrlBtn({
  icon, label, active, onClick,
}: { icon: string; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
        padding: '10px 16px', borderRadius: 10,
        border: '1px solid #374151',
        background: active ? '#1e3a2a' : '#1c1c1c',
        color: active ? '#22c55e' : '#64748b',
        cursor: 'pointer', fontSize: '0.7rem',
      }}
    >
      <span style={{ fontSize: '1.3rem' }}>{icon}</span>
      {label}
    </button>
  );
}
